import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useT } from "@/i18n/LanguageContext";
import SectionHeading from "@/components/SectionHeading";
import api from "@/lib/api";
import { asArray, asStringArray, formatCount, formatCr } from "@/lib/safeData";
import { AlertTriangle } from "lucide-react";

const STATUS_LABEL = {
  high_risk: "HIGH RISK",
  reported_cybercrime: "REPORTED TO CYBER CRIME",
  sebi_warning: "SEBI WARNING",
  multiple_complaints: "MULTIPLE COMPLAINTS",
  under_investigation: "UNDER INVESTIGATION",
};

const STATUS_CLASS = {
  high_risk: "tag-high-risk",
  reported_cybercrime: "tag-cybercrime",
  sebi_warning: "tag-sebi-warning",
  multiple_complaints: "tag-multiple",
  under_investigation: "tag-investigating",
};

const ScammerWatchlist = ({ limit = 6, showHeading = true }) => {
  const { t } = useT();
  const db = t.database || {};
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get(`/scammers?limit=${limit}`)
      .then((r) => setItems(asArray(r.data)))
      .catch(() => setItems([]));
  }, [limit]);

  const watchlist = asArray(items);
  if (!watchlist.length) return null;

  return (
    <section className={showHeading ? "mt-14" : ""} data-testid="scammer-watchlist">
      {showHeading && (
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <SectionHeading
            eyebrow="Verified Watchlist"
            title={db.watchlistTitle || "Top Reported Scammers"}
            subtitle={
              db.watchlistSubtitle ||
              "Evidence-backed entries from our verified database. Every handle here is corroborated by multiple victim reports."
            }
          />
          <Link to="/database" className="text-sm text-[#D4AF37] hover:underline whitespace-nowrap" data-testid="all-watchlist-link">
            {db.watchlistLink || "View full search"} →
          </Link>
        </div>
      )}
      <div className="surface-card overflow-hidden">
        <div className="divide-y divide-white/5">
          {watchlist.map((s, i) => (
            <Link
              key={s.id ?? `watchlist-${i}`}
              to={`/database/${s.id ?? ""}`}
              className="grid grid-cols-12 gap-3 p-4 md:p-5 items-center hover:bg-white/5 transition-colors group"
              data-testid={`watchlist-row-${s.id ?? i}`}
            >
              <div className="col-span-1 text-[10px] font-mono-alt text-white/30">#{String(i + 1).padStart(2, "0")}</div>
              <div className="col-span-12 md:col-span-4 min-w-0">
                <div className="font-display text-base font-bold text-white truncate group-hover:text-[#D4AF37] transition-colors">
                  {s.handle ?? "—"}
                </div>
                <div className="mt-0.5 text-[11px] text-white/40 font-mono-alt truncate">
                  {s.telegram_username || s.upi_id || s.company_name || s.website || "—"}
                </div>
              </div>
              <div className="col-span-7 md:col-span-4">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-mono-alt ${STATUS_CLASS[s.status_tag] || "tag-investigating"}`}>
                  <AlertTriangle className="h-3 w-3" />
                  {db.statusLabels?.[s.status_tag] || STATUS_LABEL[s.status_tag] || s.status_tag}
                </span>
                <div className="mt-1.5 text-[11px] text-white/55">
                  {formatCount(s.victim_count)} {db.victims || "victims"}
                  {asStringArray(s.scam_methods).length > 0
                    ? ` · ${asStringArray(s.scam_methods).slice(0, 2).join(" · ")}`
                    : ""}
                </div>
              </div>
              <div className="col-span-5 md:col-span-3 text-right">
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono-alt">
                  {db.lossReported || "Reported loss"}
                </div>
                <div className="text-[#FF6A60] font-bold font-mono-alt">{formatCr(s.total_loss_inr)}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ScammerWatchlist;
